import React, { useState } from 'react';
import { ChevronDown, ChevronRight, MessageCircle, Brain, Lightbulb } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import ContactModal from './ContactModal';

const AIOptimizedFAQ = () => {
  const { t } = useLanguage();
  const [openItems, setOpenItems] = useState(new Set([0])); // First item open by default
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);

  const toggleItem = (index) => {
    const newOpenItems = new Set(openItems);
    if (newOpenItems.has(index)) {
      newOpenItems.delete(index);
    } else {
      newOpenItems.add(index);
    }
    setOpenItems(newOpenItems);
  };

  const openContactModal = () => {
    setIsContactModalOpen(true);
  };

  const closeContactModal = () => {
    setIsContactModalOpen(false);
  };

  const faqData = [
    {
      icon: Brain,
      question: t('faq.valueEngine.question'),
      answer: t('faq.valueEngine.answer'),
      category: 'KI-Value-Engine',
      keywords: ['KI-Value-Engine', 'Value Dashboard', 'Portfolio-Steuerung', 'EBIT Impact']
    },
    {
      icon: Lightbulb,
      question: t('faq.valueCheck.question'),
      answer: t('faq.valueCheck.answer'),
      category: 'KI-Value-Check',
      keywords: ['KI-Value-Check', 'Reifegrad', 'Assessment', 'Executive Briefing']
    },
    {
      icon: Brain,
      question: t('faq.kiTransformation.question'),
      answer: t('faq.kiTransformation.answer'),
      category: 'KI Transformation',
      keywords: ['KI Transformation', 'AI Strategy', 'Digital Transformation']
    },
    {
      icon: Lightbulb,
      question: t('faq.customAI.question'),
      answer: t('faq.customAI.answer'),
      category: 'custom made KI Anwendungen',
      keywords: ['custom made KI Anwendungen', 'Private GPT', 'Enterprise AI']
    },
    {
      icon: MessageCircle,
      question: t('faq.aiVsDigital.question'),
      answer: t('faq.aiVsDigital.answer'),
      category: 'AI vs Digital',
      keywords: ['AI Transformation', 'Automation', 'Machine Learning']
    },
    {
      icon: Brain,
      question: t('faq.industries.question'),
      answer: t('faq.industries.answer'),
      category: 'Industries',
      keywords: ['Industry Applications', 'Manufacturing', 'Finance', 'Healthcare']
    },
    {
      icon: Lightbulb,
      question: t('faq.timeline.question'),
      answer: t('faq.timeline.answer'),
      category: 'Implementation',
      keywords: ['Project Timeline', 'Implementation Phase', 'ROI']
    },
    {
      icon: MessageCircle,
      question: t('faq.roi.question'),
      answer: t('faq.roi.answer'),
      category: 'ROI & Benefits',
      keywords: ['Return on Investment', 'Business Value', 'Cost Savings']
    }
  ];

  return (
    <section className="py-20 md:py-28 bg-surface-sunken">
      <div className="container mx-auto px-4">
        <div>
          {/* Header */}
          <div className="max-w-[64ch] mb-14">
            <h2 className="text-[clamp(1.9rem,3.6vw,3rem)] font-extrabold tracking-[-0.032em] leading-[1.06] [text-wrap:balance] text-ink mb-5">
              {t('faq.title')}
            </h2>
            <p className="text-[1.05rem] leading-relaxed text-ink-muted max-w-[62ch] leading-relaxed">
              {t('faq.subtitle')}
            </p>
          </div>

          {/* FAQ Items */}
          <div className="border-t border-hairline max-w-[1000px]">
            {faqData.map((item, index) => {
              const Icon = item.icon;
              const isOpen = openItems.has(index);
              
              return (
                <div key={index} className="border-b border-hairline">
                  <button
                    onClick={() => toggleItem(index)}
                    className="group w-full py-6 text-left flex items-start justify-between gap-6 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand rounded-lg"
                    aria-expanded={isOpen}
                  >
                    <div className="flex items-start gap-4">
                      <span className="mt-1 flex-shrink-0 text-sm font-bold tabular-nums text-brand-300">
                        {String(index + 1).padStart(2, '0')}
                      </span>
                      <div className="flex-1">
                        <h3 className="text-[1.15rem] font-bold tracking-[-0.02em] text-ink transition-colors group-hover:text-brand-300">
                          {item.question}
                        </h3>
                      </div>
                    </div>
                    <div className="flex-shrink-0 ml-3">
                      {isOpen ? (
                        <ChevronDown className="h-4 w-4 text-ink-faint transition-transform duration-200" />
                      ) : (
                        <ChevronRight className="h-4 w-4 text-ink-faint transition-transform duration-200" />
                      )}
                    </div>
                  </button>
                  
                  {isOpen && (
                    <div className="pb-7">
                      <div className="pl-10 md:pl-11">
                        <div className="prose prose-gray max-w-none">
                          <p className="max-w-[70ch] text-[0.98rem] leading-relaxed text-ink-muted">
                            {item.answer}
                          </p>
                        </div>
                        
                        {/* Keywords for AI Understanding */}
                        <div className="mt-3 flex flex-wrap gap-1.5">
                          {item.keywords.map((keyword, keyIndex) => (
                            <span 
                              key={keyIndex}
                              className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-surface-raised text-ink-muted"
                            >
                              {keyword}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* CTA Section */}
          <div className="mt-8 text-center">
            <div className="bg-brand-700 rounded-lg p-6">
              <h3 className="text-xl font-bold text-white mb-5">
                {t('faq.cta.title')}
              </h3>
              <p className="text-sm text-white/90 mb-4 max-w-[62ch]">
                {t('faq.cta.subtitle')}
              </p>
              <button 
                className="bg-surface text-brand-400 hover:bg-surface-sunken font-semibold py-2.5 px-5 rounded-lg transition-colors duration-200 text-sm"
                onClick={openContactModal}
              >
                {t('faq.cta.button')}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Contact Modal */}
      <ContactModal
        isOpen={isContactModalOpen}
        onClose={closeContactModal}
        pageSource="Homepage FAQ"
        topic="KI Transformation Beratung"
      />
    </section>
  );
};

export default AIOptimizedFAQ;